export type AIConvoThinkingLoaderPose = {
    bigRotation: number;
    bigScale: number;
    smallX: number;
    smallY: number;
    smallScale: number;
    smallInFront: boolean;
};

export type AIConvoThinkingLoaderVariant = {
    /** Length of the motion; the loader then rests in its logo pose for `pauseMs`. */
    periodMs: number;
    pauseMs: number;
    radius: number;
    /** Vertical squash of the orbit, as seen from a tilted point of view. */
    ky: number;
    tilt: number;
    /** How much the small star grows when it passes in front of the big one. */
    depth: number;
    /** `quarter` turns the big star a quarter turn per cycle; `spring` leaves its angle to the caller. */
    bigMode: 'quarter' | 'spring';
    bigBreath: number;
};

/** The logo pose: the small star tucked in the upper-right gap of the big one. */
const REST_ANGLE_DEG = -45;
export const AI_CONVO_THINKING_LOADER_BIG_RADIUS = 12;
export const AI_CONVO_THINKING_LOADER_SMALL_RADIUS = 4.2;
const BIG_TURN_DEG = 90;
const REST_RADIUS = Math.SQRT2 * 0.65 * AI_CONVO_THINKING_LOADER_BIG_RADIUS;

export const AI_CONVO_THINKING_LOADER_VARIANTS: readonly AIConvoThinkingLoaderVariant[] =
    [
        {
            periodMs: 3600,
            pauseMs: 700,
            radius: 16,
            ky: 0.45,
            tilt: -28,
            depth: 0.22,
            bigMode: 'quarter',
            bigBreath: 0.06,
        },
        {
            periodMs: 5000,
            pauseMs: 700,
            radius: 16,
            ky: 0.55,
            tilt: -18,
            depth: 0.15,
            bigMode: 'spring',
            bigBreath: 0,
        },
    ];

const smooth = (x: number): number => {
    const c = Math.min(1, Math.max(0, x));
    return c * c * (3 - 2 * c);
};

const easeInOut = (x: number): number =>
    x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;

const toRad = (deg: number): number => (deg * Math.PI) / 180;

/**
 * Where everything is `elapsedMs` into the loop. Every cycle starts and ends in
 * the logo pose (the envelope is zero there), so the loader leaves its resting
 * place, travels, and comes back to it.
 */
export function getThinkingLoaderPose(
    elapsedMs: number,
    variant: AIConvoThinkingLoaderVariant,
    springRotation = 0,
): AIConvoThinkingLoaderPose {
    const cycle = variant.periodMs + variant.pauseMs;
    const t = ((elapsedMs % cycle) + cycle) % cycle;
    const u = t >= variant.periodMs ? 0 : t / variant.periodMs;

    const envelope = smooth(u / 0.2) * smooth((1 - u) / 0.2);
    const angle = REST_ANGLE_DEG + 360 * easeInOut(u);
    const radius = REST_RADIUS + (variant.radius - REST_RADIUS) * envelope;
    const ky = 1 + (variant.ky - 1) * envelope;
    const tilt = toRad(variant.tilt * envelope);

    const px = radius * Math.cos(toRad(angle));
    const py = radius * Math.sin(toRad(angle)) * ky;
    const depth = Math.sin(toRad(angle));

    return {
        bigRotation:
            variant.bigMode === 'spring'
                ? springRotation
                : BIG_TURN_DEG * easeInOut(u),
        bigScale: 1 + variant.bigBreath * Math.sin(Math.PI * u),
        smallX: px * Math.cos(tilt) - py * Math.sin(tilt),
        smallY: px * Math.sin(tilt) + py * Math.cos(tilt),
        smallScale: 1 + variant.depth * depth * envelope,
        smallInFront: depth > 0 || envelope < 0.1,
    };
}

const SPRING_STIFFNESS = 18;
const SPRING_DAMPING = 6;

export type AIConvoThinkingLoaderSpring = { angle: number; velocity: number };

/** Moves the big star towards `target` degrees with a damped spring; `dt` is in seconds. */
export function stepThinkingLoaderSpring(
    spring: AIConvoThinkingLoaderSpring,
    target: number,
    dt: number,
): AIConvoThinkingLoaderSpring {
    const velocity =
        spring.velocity +
        ((target - spring.angle) * SPRING_STIFFNESS -
            spring.velocity * SPRING_DAMPING) *
            dt;

    return { angle: spring.angle + velocity * dt, velocity };
}
